import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { theme } from '../theme';
import { useIsMobile } from '../hooks/useMediaQuery';

const LOG_PAGE_SIZE = 50;

const LOG_STATUS_COLORS = {
  sent: theme.colors.primary,
  delivered: '#06b6d4',
  read: theme.colors.success,
  failed: theme.colors.error,
};

const CAMPAIGN_STATUS_COLORS = {
  SENDING: '#facc15',
  COMPLETED: theme.colors.success,
  FAILED: theme.colors.error,
};

function MarketingLogs({ token }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [campaign, setCampaign] = useState(null);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const totalPages = Math.max(1, Math.ceil(total / LOG_PAGE_SIZE));

  useEffect(() => {
    setLoading(true);
    fetch(`/api/marketing/${id}/logs?page=${page}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) { setCampaign(d.campaign); setLogs(d.logs || []); setTotal(d.total || 0); }
      })
      .finally(() => setLoading(false));
  }, [id, page, token]);

  const sentPct = campaign?.total_contacts > 0
    ? Math.round((campaign.sent_count / campaign.total_contacts) * 100) : 0;
  const failedPct = campaign?.total_contacts > 0
    ? Math.round((campaign.failed_count / campaign.total_contacts) * 100) : 0;

  if (loading && !campaign) {
    return (
      <main style={{ padding: isMobile ? '1rem' : '2rem 1.5rem', maxWidth: '900px', margin: '0 auto' }}>
        <p style={{ color: theme.colors.textMuted, textAlign: 'center', padding: '4rem' }}>Cargando...</p>
      </main>
    );
  }

  return (
    <main style={{ padding: isMobile ? '1rem' : '2rem 1.5rem', maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ backgroundColor: theme.colors.surface, borderRadius: '14px', border: `1px solid ${theme.colors.border}`, padding: isMobile ? '1.25rem' : '2rem', boxShadow: `0 4px 24px ${theme.colors.glowSubtle}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <h1 style={{ color: theme.colors.primary, fontSize: '1.4rem', margin: '0 0 0.5rem' }}>{campaign?.name}</h1>
            <span style={{
              display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700',
              backgroundColor: `${CAMPAIGN_STATUS_COLORS[campaign?.status] || theme.colors.textMuted}22`,
              color: CAMPAIGN_STATUS_COLORS[campaign?.status] || theme.colors.textMuted,
              border: `1px solid ${CAMPAIGN_STATUS_COLORS[campaign?.status] || theme.colors.textMuted}`,
            }}>{campaign?.status}</span>
          </div>
          <button onClick={() => navigate('/publicidad')} style={{ background: 'none', border: 'none', color: theme.colors.textMuted, cursor: 'pointer', fontSize: '0.9rem' }}>← Volver</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          {[
            { label: 'Total', value: campaign?.total_contacts || 0, color: theme.colors.text },
            { label: `Enviados (${sentPct}%)`, value: campaign?.sent_count || 0, color: theme.colors.success },
            { label: `Fallidos (${failedPct}%)`, value: campaign?.failed_count || 0, color: theme.colors.error },
          ].map(stat => (
            <div key={stat.label} style={{ backgroundColor: theme.colors.background, borderRadius: '10px', border: `1px solid ${theme.colors.border}`, padding: '1rem', textAlign: 'center' }}>
              <p style={{ color: theme.colors.textMuted, fontSize: '0.8rem', margin: '0 0 0.3rem' }}>{stat.label}</p>
              <p style={{ color: stat.color, fontSize: '1.5rem', fontWeight: '700', margin: 0 }}>{stat.value.toLocaleString('es-CO')}</p>
            </div>
          ))}
        </div>

        {loading ? (
          <p style={{ color: theme.colors.textMuted, textAlign: 'center', padding: '2rem' }}>Cargando...</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
              <thead>
                <tr>
                  {['Teléfono', 'Estado', 'Error', 'Fecha'].map(col => (
                    <th key={col} style={{ textAlign: 'left', padding: '10px 12px', borderBottom: `1px solid ${theme.colors.border}`, color: theme.colors.primary, fontWeight: '600', whiteSpace: 'nowrap' }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: theme.colors.textMuted }}>Sin resultados aún. Vuelve en unos minutos.</td></tr>
                ) : logs.map(log => (
                  <tr key={log.id} style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
                    <td style={{ padding: '9px 12px', color: theme.colors.text }}>{log.phone}</td>
                    <td style={{ padding: '9px 12px' }}>
                      <span style={{
                        display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700',
                        backgroundColor: `${LOG_STATUS_COLORS[log.status] || theme.colors.textMuted}22`,
                        color: LOG_STATUS_COLORS[log.status] || theme.colors.textMuted,
                        border: `1px solid ${LOG_STATUS_COLORS[log.status] || theme.colors.textMuted}`,
                      }}>{log.status}</span>
                    </td>
                    <td style={{ padding: '9px 12px', color: theme.colors.error, maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.82rem' }}>
                      {log.error_message || '—'}
                    </td>
                    <td style={{ padding: '9px 12px', color: theme.colors.textMuted, whiteSpace: 'nowrap' }}>
                      {new Date(log.created_at).toLocaleDateString('es-CO')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1.2rem' }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '6px 16px', borderRadius: '6px', border: `1px solid ${theme.colors.border}`, cursor: page === 1 ? 'not-allowed' : 'pointer', backgroundColor: 'transparent', color: page === 1 ? theme.colors.textMuted : theme.colors.text, fontSize: '0.88rem' }}>← Anterior</button>
          <span style={{ color: theme.colors.textMuted, fontSize: '0.88rem' }}>Página {page} de {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ padding: '6px 16px', borderRadius: '6px', border: `1px solid ${theme.colors.border}`, cursor: page === totalPages ? 'not-allowed' : 'pointer', backgroundColor: 'transparent', color: page === totalPages ? theme.colors.textMuted : theme.colors.text, fontSize: '0.88rem' }}>Siguiente →</button>
        </div>
      </div>
    </main>
  );
}

export default MarketingLogs;
